/**
 * AddCxP.js — Formulario nueva Factura CxP
 * UI idéntico a AddInvoices: invoice-container (2fr 1fr), invoice-details (izq),
 * filial-details (der), invoice-footer (bottom full-width).
 * CSS: addInvoice.module.css (mismas clases que AddInvoices).
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../invoices/styles/addInvoice.module.css";
import { MonedaSelector } from "../../components/MonedaSelector";
import { IvaSelector } from "../../components/IvaSelector";
import { calcularIva } from "../../shared/iva";
import { useTipoCambio } from "../../services/useTipoCambio";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import RelacionesAlert from "../../../../shared/components/RelacionesAlert";

function addDays(dateStr, days) {
  const d = new Date(dateStr); d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

const fmt = (n) => Number(n || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

export default function AddCxP() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

  function conceptoAuto(nombreGasto, fechaEmision) {
    if (!nombreGasto) return "";
    let month, year;
    if (fechaEmision) {
      [year, month] = fechaEmision.split("-").map(Number); month -= 1;
    } else { const d = new Date(); month = d.getMonth(); year = d.getFullYear(); }
    const date = new Date(year, month, 1);
    const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-CR";
    const monthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
    return `${nombreGasto} — ${monthName} ${year}`;
  }

  const [form, setForm] = useState({
    fecha_emision:           today,
    fecha_vencimiento:       today,
    concepto:                "",
    monto:                   "",
    numero_doc_proveedor:    "",
    cuenta_proveedor:        null,
    cuenta_proveedor_nombre: "",
    cuenta_gasto:            null,
    cuenta_gasto_nombre:     "",
    moneda:                  "CRC",
    tipo_cambio:             1,
    // ── IVA — `monto` es el valor digitado; iva_modo='INCLUIDO' ya trae IVA ──
    iva_incluido:            true,
    tasa_iva:                13,
    iva_modo:                "SUMAR",
  });
  const { tipoCambio, loading: loadingTC } = useTipoCambio();
  const multimoneda = useMultimoneda();

  // Auto-populate TC from BCCR (tipo_compra) siempre que cargue, sin importar la moneda
  useEffect(() => {
    if (tipoCambio?.tipo_compra && (!form.tipo_cambio || form.tipo_cambio === 1)) {
      setForm(p => ({ ...p, tipo_cambio: Number(tipoCambio.tipo_compra) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio, form.tipo_cambio]);

  const [cuentasPasivo,  setCuentasPasivo]  = useState([]);
  const [cuentasGasto,   setCuentasGasto]   = useState([]);
  const [proximoNumero,  setProximoNumero]  = useState("CP-…");
  const [errors,        setErrors]        = useState({});
  const [saving,        setSaving]        = useState(false);
  const [apiErr,        setApiErr]        = useState("");
  const [resultado,     setResultado]     = useState(null);

  useEffect(() => {
    apiFetch("/api/finance/cxp/proximo-numero/")
      .then(r => r.json())
      .then(d => setProximoNumero(d.proximo ?? "CP-…"))
      .catch(() => {});

    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&excluir_a_favor=1")
      .then(r => r.json())
      .then(d => setCuentasPasivo(Array.isArray(d) ? d : (d.results ?? [])));

    apiFetch("/api/finance/catalogo/detalle/?tipo=GASTO")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasGasto(list);
        if (list.length > 0) {
          const p = list[0];
          setForm(prev => ({ ...prev,
            cuenta_gasto: p.id, cuenta_gasto_nombre: p.nombre,
            concepto: conceptoAuto(p.nombre, prev.fecha_emision),
          }));
        }
      });
  }, []);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  function handleFechaEmision(fecha) {
    setForm(p => ({
      ...p, fecha_emision: fecha,
      fecha_vencimiento: fecha ?? "",
      concepto: conceptoAuto(p.cuenta_gasto_nombre, fecha),
    }));
  }

  function handleCuentaGasto(e) {
    const id = Number(e.target.value);
    const obj = cuentasGasto.find(c => c.id === id);
    setForm(p => ({
      ...p, cuenta_gasto: id || null, cuenta_gasto_nombre: obj?.nombre ?? "",
      concepto: conceptoAuto(obj?.nombre ?? "", p.fecha_emision),
    }));
  }

  function handleCuentaProveedor(e) {
    const id = Number(e.target.value);
    const obj = cuentasPasivo.find(c => c.id === id);
    setForm(p => ({ ...p, cuenta_proveedor: id || null, cuenta_proveedor_nombre: obj?.nombre ?? "" }));
  }

  function validate() {
    const e = {};
    if (!form.numero_doc_proveedor.trim()) e.numero_doc_proveedor = t("cxp.nDocReq");
    if (!form.cuenta_proveedor)            e.cuenta_proveedor     = t("cxp.proveedorReq");
    if (!form.cuenta_gasto)                e.cuenta_gasto         = t("cxp.tipoGastoReq");
    if (!form.concepto.trim())             e.concepto             = t("cxp.conceptoReq");
    if (!form.fecha_emision)               e.fecha_emision        = t("cxp.fechaEmisionReq");
    if (!form.fecha_vencimiento)           e.fecha_vencimiento    = t("cxp.fechaVencimientoReq");
    if (form.fecha_emision && form.fecha_vencimiento &&
        form.fecha_vencimiento < form.fecha_emision)
                                           e.fecha_vencimiento    = t("cxp.fechaVencimientoPosterior");
    if (!form.monto || Number(form.monto) <= 0) e.monto           = t("cxp.montoReq");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setApiErr("");
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiFetch("/api/finance/cxp/", {
        method: "POST",
        body: JSON.stringify({
          fecha_emision:        form.fecha_emision,
          fecha_vencimiento:    form.fecha_vencimiento,
          concepto:             form.concepto.trim(),
          monto:                Number(form.monto),
          numero_doc_proveedor: form.numero_doc_proveedor.trim(),
          cuenta_proveedor:     form.cuenta_proveedor,
          cuenta_gasto:         form.cuenta_gasto,
          moneda:               form.moneda,
          tipo_cambio:          Number(form.tipo_cambio) || 1,
          iva_incluido:         form.iva_incluido,
          tasa_iva:             form.iva_incluido ? Number(form.tasa_iva) : 0,
          iva_modo:             form.iva_modo,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
      }
      setResultado(await res.json());
      resetForm();
      apiFetch("/api/finance/cxp/proximo-numero/")
        .then(r => r.json()).then(d => setProximoNumero(d.proximo ?? "CP-…"));
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  function resetForm() {
    const autoConcepto = conceptoAuto(form.cuenta_gasto_nombre, today);
    setForm({ fecha_emision: today, fecha_vencimiento: addDays(today, 30),
      concepto: autoConcepto, monto: "", numero_doc_proveedor: "",
      cuenta_proveedor: null, cuenta_proveedor_nombre: "",
      cuenta_gasto: form.cuenta_gasto, cuenta_gasto_nombre: form.cuenta_gasto_nombre,
      moneda: "CRC", tipo_cambio: 1,
      iva_incluido: true, tasa_iva: 13, iva_modo: "SUMAR",
    });
    setErrors({}); setApiErr("");
  }

  function handleReset(e) {
    e.preventDefault();
    resetForm();
    setResultado(null);
  }

  const errorCount = Object.keys(errors).length;
  // `monto` es el valor digitado; el desglose depende del modo.
  const { base: baseNum, iva: ivaNum, total: montoNum } =
    calcularIva(Number(form.monto) || 0, form.tasa_iva, form.iva_incluido, form.iva_modo);
  const aplicaIva = !!form.iva_incluido && ivaNum > 0;

  return (
    <>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={styles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <Link to="/finanzas/cxp/facturas">
          <span className={styles.inactive}> {t("cxp.breadcrumbFacturas")} /</span>
        </Link>
        <span className={styles.active}> {t("common.agregar")}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("cxp.generarFactura")}</h2>

      {/* Aviso si faltan relaciones necesarias para CxP (proveedor + IVA) */}
      <RelacionesAlert
        campos={[
          "rel_cuenta_proveedores",
          ...(form.iva_incluido ? ["rel_cuenta_iva_favor"] : []),
        ]}
      />

      {/* Banner de éxito */}
      {resultado && (
        <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px", marginBottom:16, marginTop:12,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ color:"#4ade80", fontWeight:500, fontSize:"0.9rem" }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight:8 }}/>
            {t("cxp.facturaCxPRegistrada", { numero: resultado.numero_factura })}
          </span>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={() => navigate("/finanzas/cxp/facturas")}
              style={{ padding:"6px 14px", background:"rgba(249,115,22,0.15)",
                border:"1px solid rgba(249,115,22,0.4)", borderRadius:7, color:"#f97316",
                fontSize:"0.83rem", fontWeight:600, cursor:"pointer" }}>
              {t("cxp.verCxP")}
            </button>
            <button onClick={() => setResultado(null)}
              style={{ background:"none", border:"none", color:"#4ade80", cursor:"pointer", fontSize:"1rem" }}>✕</button>
          </div>
        </div>
      )}

      {/* Error count banner */}
      {errorCount > 0 && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8,
            color:"#f87171", fontWeight:600, marginBottom:6 }}>
            <i className="fa-solid fa-triangle-exclamation"/>
            {t("common.hay")} {errorCount} {t("cxp.camposConError")}
          </div>
          <ul style={{ margin:0, paddingLeft:20, color:"#fca5a5", fontSize:"0.83rem" }}>
            {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>
      )}

      {/* API error */}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16,
          color:"#f87171", fontSize:"0.88rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles["invoice-container"]}>

          {/* ── Izquierda: Detalles del Documento ── */}
          <div className={styles["invoice-details"]}>
            <div className={styles["invoice-details-header"]}>
              <h1>
                <i className={`fa-solid fa-file-invoice-dollar ${styles["icon-invoice-details"]}`}/>
                {t("cxp.detallesDocumento")}
              </h1>
            </div>
            <div className={styles["invoice-details-body"]} style={{ marginTop:14 }}>

              {/* N° Doc Proveedor */}
              <div>
                <label>{t("cxp.numDocProveedor")} <span style={{ color:"#f87171" }}>*</span></label>
                <input className="form-control" type="text"
                  placeholder={t("cxp.nDocPlaceholder")}
                  value={form.numero_doc_proveedor}
                  onChange={e => set("numero_doc_proveedor", e.target.value)}
                  style={errors.numero_doc_proveedor ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.numero_doc_proveedor}/>
                <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3,
                  display:"flex", alignItems:"center", gap:5 }}>
                  <i className="fa-solid fa-hashtag" style={{ fontSize:"0.65rem" }}/>
                  {t("cxp.internoAutoNum")}
                  <span style={{ fontFamily:"monospace", color:"#f97316",
                    fontWeight:600 }}>{proximoNumero}</span>
                </div>
              </div>

              {/* Gasto (DEBE) */}
              <div>
                <label>{t("cxp.gasto")} <span style={{ color:"#f87171" }}>*</span></label>
                <select className="form-select"
                  value={form.cuenta_gasto ?? ""}
                  onChange={handleCuentaGasto}
                  style={errors.cuenta_gasto ? { borderColor:"#ef4444" } : undefined}>
                  <option value="">{t("cxp.seleccionaTipoGasto")}</option>
                  {cuentasGasto.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_gasto}/>
              </div>

              {/* Concepto (auto, editable) */}
              <div>
                <label style={{ display:"flex", alignItems:"center", gap:6 }}>
                  {t("cxp.concepto")}
                  <span style={{ fontSize:"0.68rem", color:"var(--text-secondary)",
                    background:"var(--bg-card)", border:"1px solid var(--border-card)",
                    borderRadius:4, padding:"1px 6px", fontWeight:400 }}>
                    {t("cxp.autoBadge")}
                  </span>
                </label>
                <input className="form-control"
                  placeholder={t("cxp.conceptoPlaceholder")}
                  value={form.concepto}
                  onChange={e => set("concepto", e.target.value)}
                  style={errors.concepto ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.concepto}/>
                <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3 }}>
                  {t("cxp.editarManualmente")}
                </div>
              </div>

              {/* Fechas */}
              <div className="row">
                <div className="col">
                  <label>{t("cxp.fechaEmision")} <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha_emision}
                    onChange={e => handleFechaEmision(e.target.value)}
                    style={errors.fecha_emision ? { borderColor:"#ef4444" } : undefined}/>
                  <FieldError msg={errors.fecha_emision}/>
                </div>
                <div className="col">
                  <label>{t("cxp.fechaVencimiento")} <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha_vencimiento}
                    onChange={e => set("fecha_vencimiento", e.target.value)}
                    style={errors.fecha_vencimiento ? { borderColor:"#ef4444" } : undefined}/>
                  <FieldError msg={errors.fecha_vencimiento}/>
                </div>
              </div>

              {/* Monto */}
              <div>
                <label>{t("cxp.monto")} <span style={{ color:"#f87171" }}>*</span></label>
                <input type="number" className="form-control"
                  placeholder="0.00" min="0.01" step="0.01"
                  value={form.monto}
                  onChange={e => set("monto", e.target.value)}
                  style={errors.monto ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.monto}/>
                <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3 }}>
                  {form.iva_incluido && form.iva_modo === "INCLUIDO"
                    ? <>Ingresá el monto <strong>con IVA incluido</strong> (total). El sistema desglosa la base y el IVA.</>
                    : <>Ingresá el monto <strong>sin IVA</strong> (base). El sistema calcula el IVA y el total.</>}
                </div>
              </div>

              {/* IVA */}
              <IvaSelector
                ivaIncluido={form.iva_incluido}
                tasaIva={form.tasa_iva}
                onIvaIncluidoChange={(v) => set("iva_incluido", v)}
                onTasaChange={(v) => set("tasa_iva", v)}
                base={Number(form.monto) || 0}
                moneda={form.moneda}
                modo={form.iva_modo || "SUMAR"}
                onModoChange={(v) => set("iva_modo", v)}
              />

              {/* Moneda — solo visible cuando multimoneda está activo */}
              {multimoneda && (
                <MonedaSelector
                  moneda={form.moneda}
                  onMonedaChange={(m) => set("moneda", m)}
                  tipoCambio={form.tipo_cambio}
                  onTCChange={(tc) => set("tipo_cambio", tc)}
                  loadingTC={loadingTC}
                  tcFuente={tipoCambio?.fuente}
                  monto={Number(form.monto) || 0}
                />
              )}

            </div>
          </div>

          {/* ── Derecha: Proveedor + Resumen ── */}
          <div className={styles["filial-details"]}>
            <div className={styles["filial-details-header"]}>
              <h1>
                <i className={`fa-solid fa-house ${styles["icon-filial-details"]}`}/>
                {t("cxp.infoProveedor")}
              </h1>
            </div>

            <div className={styles["invoice-details-body"]} style={{ marginTop:14 }}>

              {/* Proveedor (HABER) */}
              <label>{t("cxp.proveedor")} <span style={{ color:"#f87171" }}>*</span></label>
              <select className="form-select"
                value={form.cuenta_proveedor ?? ""}
                onChange={handleCuentaProveedor}
                style={errors.cuenta_proveedor ? { borderColor:"#ef4444" } : undefined}>
                <option value="">{t("cxp.nombreProveedor")}</option>
                {cuentasPasivo.map(c => (
                  <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                ))}
              </select>
              <FieldError msg={errors.cuenta_proveedor}/>

              <label style={{ marginTop: 12 }}>{t("cxp.gasto")}</label>
              <input className="form-control"
                value={form.cuenta_gasto_nombre}
                disabled
                style={{ opacity: 0.7 }}/>

              <div className={styles["filial-details-summary"]}>
                <div className={styles["filial-summary-relevant"]}>
                  <h2>{t("registros.resumenRapido")}</h2>

                  {/* Moneda */}
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{t("registros.moneda")}</span>
                    <span style={{
                      fontWeight: 700,
                      color: form.moneda === "USD" ? "#60a5fa" : "#f97316",
                      background: form.moneda === "USD" ? "rgba(96,165,250,0.12)" : "rgba(249,115,22,0.12)",
                      border: `1px solid ${form.moneda === "USD" ? "rgba(96,165,250,0.3)" : "rgba(249,115,22,0.3)"}`,
                      borderRadius: 4,
                      padding: "1px 8px",
                      fontSize: "0.8rem",
                    }}>
                      {form.moneda}
                    </span>
                  </div>

                  {/* Tipo de cambio */}
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{t("registros.tipoCambio")}</span>
                    <span style={{ fontFamily: "monospace", color: "var(--text-secondary)" }}>
                      ₡{fmt(Number(form.tipo_cambio) || 1)} / USD
                    </span>
                  </div>

                  {/* Desglose IVA */}
                  {aplicaIva && (
                    <>
                      <div className="d-flex justify-content-between align-items-center">
                        <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Base gravable</span>
                        <span style={{ fontFamily: "monospace", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                          {form.moneda === "USD" ? "$" : "₡"}{fmt(baseNum)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                          IVA ({Number(form.tasa_iva)}%)
                        </span>
                        <span style={{ fontFamily: "monospace", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                          {form.moneda === "USD" ? "$" : "₡"}{fmt(ivaNum)}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Equivalente en colones (si USD) */}
                  {form.moneda === "USD" && (
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                        {t("registros.equivColones")}
                      </span>
                      <span style={{ fontFamily: "monospace", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                        ₡{fmt(montoNum * (Number(form.tipo_cambio) || 1))}
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles["filial-summary-important"]} style={{ marginTop:12 }}>
                  <h2>{t("cxp.totalAPagar")}</h2>
                  {form.moneda === "USD" ? (
                    <div style={{ textAlign: "right" }}>
                      <span style={{ color: "#60a5fa", fontWeight: 700 }}>
                        ${fmt(montoNum)} USD
                      </span>
                      <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontWeight: 400 }}>
                        ≈ ₡{fmt(montoNum * (Number(form.tipo_cambio) || 1))}
                      </div>
                    </div>
                  ) : (
                    <span>₡ {fmt(montoNum)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={styles["invoice-footer"]}>
            <div className={styles.actions}>
              <button type="button" onClick={handleReset}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}>
                <i className="fa-solid fa-rotate-left"/> {t("cxp.limpiarFormulario")}
              </button>
              <button type="submit" disabled={saving}
                className={`${styles.btn} ${styles["btn-save"]}`}
                style={{ opacity: saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
                  : <><i className="fa-solid fa-lock"/> {t("common.guardar")}</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}
